import { Component, Input, Output, EventEmitter, forwardRef, OnInit, SimpleChanges, OnChanges } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, AbstractControl, ValidationErrors, Validator, NG_VALIDATORS } from '@angular/forms';
import { CeveService } from '../../catalog/ceve/ceve.service';
import { GenericService } from '../services/GenericServices';
import { Unit } from '../models/Unit.Model';
import { AphPhoneCodesService, CountryCode } from '../aph-phone-codes/aph-phone-codes.service';


@Component({
  selector: 'aph-custom-text',
  templateUrl: './aph-custom-text.component.html',
  styleUrls: ['./aph-custom-text.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AphCustomTextComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => AphCustomTextComponent),
      multi: true
    }
  ]
})

export class AphCustomTextComponent implements ControlValueAccessor, Validator, OnChanges {

  // Configuraciones Generales
  static nextId = 0;
  @Input() label: string = '';
  @Input() id: string = `smart-input-${AphCustomTextComponent.nextId++}`;
  @Input() type: 'text' | 'numeric' | 'currency' | 'phone' | 'zip' | 'email' = 'text';
  @Input() placeholder: string = '';
  @Input() disabled: boolean = false;
  @Input() required: boolean = false;
  @Input() org: string = '';
  @Input() unitId: string = '';
  // Text Config
  @Input() minLength: number = 0;
  @Input() maxLength: number = 500;

  // Numeric Config
  @Input() min: number = -999999999999;
  @Input() max: number = 999999999999;
  @Input() allowDecimal: boolean = false;
  @Input() allowNegative: boolean = false;
  @Input() useThousandsSeparator: boolean = false;

  @Output() zipDataFound = new EventEmitter<any>();

  @Output() changed = new EventEmitter<any>();

  displayValue: string = ''; 
  innerValue: any = '';      
  isInvalid: boolean = false;
  errorMsg: string = '';
  selectedCountry: CountryCode | null = null;
  onChange: any = () => {};
  onTouched: any = () => {};

  constructor(
    private ceveService: CeveService,
    private apiService: GenericService,
    private countryService: AphPhoneCodesService
  ) {}

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (this.type.length > 0 && (changes.unitId || changes.org)) {
      if(this.type === 'phone' && this.org.length > 0 && this.unitId.length > 0) {
        let data = await this.apiService.getRequest(`unit/${this.org}/${this.unitId}/parents/PAIS`, {}, 0).toPromise() as Unit[];
        if (data != null && data.length > 0) {
            const allCountries = this.countryService.getAllCountries();
            // Seleccionamos uno por defecto si no encontramos el código del país en la lista
            this.selectedCountry = allCountries.find(c => c.code === data[0].UnitCode) || allCountries[0];
        }
      }else{
        this.selectedCountry ="52" as any; // Por defecto México si no se configura
      }
      this.formatOnBlurLogic(this.innerValue);
    }
  }

  getMaxLength(): number {
    switch (this.type) {
      case 'phone':
        // +52 (3) + espacio (1) + 3 dígitos + espacio (1) + 3 dígitos + espacio (1) + 4 dígitos
        // Total: 16 caracteres para el formato: "+52 722 324 4878"
        return 16;
      
      case 'zip':
        // Estricto a 5 números para México
        return 5;
      case 'email':
        return 255;
      case 'text':
        // Respeta la configuración que venga del padre
        return this.maxLength;
      case 'numeric':
        return this.maxLength > 22 ? 22 : this.maxLength;
      case 'currency':
        // Para números y moneda, damos holgura para comas y decimales.
        // Si el usuario configuró un maxLength específico en el padre, lo usamos,
        // si no, devolvemos un valor alto (ej. 15) para no bloquear la escritura de cifras grandes.
        return this.maxLength > 15 ? 15 : this.maxLength;
        
      default:
        return this.maxLength;
    }
  }

  // --- Control Value Accessor Methods ---
    
  writeValue(value: any): void {
    if (value !== undefined && value !== null) {
      this.innerValue = value;
      // AQUÍ: Si viene un valor inicial, lo formateamos de inmediato
      this.formatOnBlurLogic(value); 
    }
  }

  registerOnChange(fn: any): void { this.onChange = fn; }
  registerOnTouched(fn: any): void { this.onTouched = fn; }
  setDisabledState(isDisabled: boolean): void { this.disabled = isDisabled; }

  validate(control: AbstractControl): ValidationErrors | null {
    return this.isInvalid ? { invalidFormat: true } : null;
  }

  // --- 2. Manejo de Eventos ---

  // VALIDACIÓN DE TECLAS (Evita que escriban letras en campos numéricos)
  onKeyPress(event: KeyboardEvent) {
    // 1. Si es email, bloqueamos el espacio
    if (this.type === 'email' && event.key === ' ') {
      event.preventDefault();
      return false;
    }
    
    // Añadimos 'email' para que no bloquee letras ni caracteres especiales
    if (this.type === 'text' || this.type === 'email') return true; 

    const inputChar = event.key;

    const pattern = /[0-9]/;

    // 1. Permitir teclas de acción del sistema (solo si no es un carácter imprimible largo)
    // Nota: 'Backspace', 'ArrowLeft', 'Tab', etc., no disparan 'keypress' en la mayoría 
    // de navegadores modernos, pero los incluimos por compatibilidad.
    const allowedKeys = ['Backspace', 'ArrowLeft', 'ArrowRight', 'Tab', 'Delete', 'Enter'];
    if (allowedKeys.includes(inputChar)) return true;

    // 2. Lógica para Numérico / Moneda
    if (this.type === 'numeric' || this.type === 'currency') {
      const inputElement = event.target as HTMLInputElement;
      const currentVal = inputElement.value;

      // Permitir signo negativo '-' solo al inicio
      if (this.allowNegative && inputChar === '-') {
        if (currentVal === '' || inputElement.selectionStart === 0) {
          return true;
        }
      }

      // Permitir punto decimal '.' solo si está habilitado y no existe uno previo
      if (this.allowDecimal && inputChar === '.') {
        if (currentVal.indexOf('.') === -1) {
          return true;
        } else {
          event.preventDefault();
          return false;
        }
      }
    }

    // 3. Validación general para números (0-9)
    // Esto aplica para Numeric, Currency, Phone y Zip
    if (!pattern.test(inputChar)) {
      event.preventDefault();
      return false;
    }

    return true;
  }

  // AL ENTRAR (FOCUS): Limpiamos el formato para editar
  onFocus() {
    if (!this.innerValue) return;

    // Convertimos el valor interno a string simple para edición
    if (this.type === 'currency' || (this.type === 'numeric' && this.useThousandsSeparator)) {
        this.displayValue = this.innerValue.toString();
    } 
    else if (this.type === 'phone') {
        // Quitamos el prefijo 52 si lo guardamos internamente, o limpiamos espacios
        // Asumiendo que guardamos "521234567890", mostramos "1234567890" para editar
        let raw = this.innerValue.toString();
        if(raw.startsWith(this.selectedCountry.dial_code)) raw = raw.substring(2);
        this.displayValue = raw;
    }
  }

  // AL ESCRIBIR (INPUT): Actualizamos el valor interno en tiempo real
  onInput(event: any) {
    let val = event.target.value;
    this.isInvalid = false;
    
    // Para numéricos, actualizamos el innerValue parseando
    if (this.type === 'numeric' || this.type === 'currency') {
      // Permitimos valores temporales como "1." o "-" mientras escribe
      const parsed = parseFloat(val.replace(/[^0-9.-]+/g,"")); 
      this.innerValue = isNaN(parsed) ? (val === '-' || val === '.' ? val : '') : parsed;
    } else {
      this.innerValue = val;
    }
    
    this.onChange(this.innerValue);
    this.changed.emit(this.innerValue);
  }

  // También debemos emitir en onBlur por si el formateo final cambió el valor
  onBlur(event: any) {
    this.onTouched();
    const oldVal = this.innerValue;
    this.formatOnBlurLogic(this.innerValue);
    
    // Si el valor cambió tras el formateo (ej: validación de min/max), emitimos
    if (oldVal !== this.innerValue) {
      this.changed.emit(this.innerValue);
    }
  }

  // --- 3. Lógica de Formateo y Validación ---

  private formatOnBlurLogic(value: any) {
    if (value === null || value === undefined || value === '') {
        this.displayValue = '';
        return;
    }
    
    const valString = value.toString();

    switch (this.type) {
      case 'numeric':
        this.handleNumericFormat(valString);
        break;
      case 'currency':
        this.handleCurrencyFormat(valString);
        break;
      case 'phone':
        this.handlePhoneFormat(valString);
        break;
      case 'zip':
        this.handleZipFormat(valString);
        break;
      case 'text':
        this.handleTextFormat(valString);
        break;
      case 'email':
        this.handleEmailFormat(valString); // <-- NUEVO
        break;
    }
  }

  private handleEmailFormat(val: string) {
    // Limpiamos espacios en blanco al inicio o final (muy común al copiar/pegar)
    const cleanEmail = val.trim();
    
    // Expresión regular estándar para correos
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    
    if (cleanEmail.length > 0 && !emailRegex.test(cleanEmail)) {
      this.isInvalid = true;
      this.errorMsg = 'Formato de correo electrónico inválido';
    } else {
      // Reutilizamos la validación de longitud máxima del texto si es necesario
      if (cleanEmail.length > this.maxLength) {
        this.isInvalid = true;
        this.errorMsg = `Máximo ${this.maxLength} caracteres`;
      }
    }

    this.innerValue = cleanEmail;
    this.onChange(this.innerValue);
    this.displayValue = cleanEmail;
  }

  private handleNumericFormat(val: string) {
    let num = parseFloat(val);
    if (isNaN(num)) return; // Si no es número, no tocamos

    // Validaciones de rango
    if (!this.allowNegative && num < 0) num = Math.abs(num);
    if (!this.allowDecimal) num = Math.trunc(num);
    
    if (num < this.min) { this.isInvalid = true; this.errorMsg = `Mínimo: ${this.min}`; }
    if (num > this.max) { this.isInvalid = true; this.errorMsg = `Máximo: ${this.max}`; }

    this.innerValue = num; // Guardamos número limpio
    this.onChange(this.innerValue);

    // Formateo visual
    if (this.useThousandsSeparator) {
      this.displayValue = new Intl.NumberFormat('en-US', {
        maximumFractionDigits: this.allowDecimal ? 10 : 0
      }).format(num);
    } else {
      this.displayValue = num.toString();
    }
  }

  private handleCurrencyFormat(val: string) {
    let num = parseFloat(val.replace(/[^0-9.-]+/g,"")); // Limpieza extra por si acaso
    if (isNaN(num)) num = 0;

    this.innerValue = num;
    this.onChange(this.innerValue);

    this.displayValue = new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 2
    }).format(num);
  }

  private async handlePhoneFormat(val: string) {
    
    // Limpiamos todo lo no numérico
    let clean = val.replace(/\D/g, '');

    // Lógica inteligente: Si el usuario borró el prefijo o escribió solo 10 nums
    if (this.selectedCountry === null) {
      if(this.type === 'phone' && this.org.length > 0 && this.unitId.length > 0) {
        let data = await this.apiService.getRequest(`unit/${this.org}/${this.unitId}/parents/PAIS`, {}, 0).toPromise() as Unit[];
        if (data != null && data.length > 0) {
            const allCountries = this.countryService.getAllCountries();
            // Seleccionamos uno por defecto si no encontramos el código del país en la lista
            this.selectedCountry = allCountries.find(c => c.code === data[0].UnitCode) || allCountries[0];
        }
      }else{
        this.selectedCountry ="52" as any; // Por defecto México si no se configura
      }
      // Es un número válido de 10 dígitos
    } 
    
    if (clean.startsWith(this.selectedCountry.dial_code) && clean.length === 12) {
        clean = clean.substring(2); // Normalizamos quitando el 52 para procesar
    } else if ( clean.length !== 10){
        if(clean.length > 0) {
            this.isInvalid = true;
            this.errorMsg = 'Debe tener 10 dígitos';
        }
        this.displayValue = clean; // Dejamos lo que escribió si está mal
        return;
    }

    // Guardamos valor con formato internacional plano
    this.innerValue = `${this.selectedCountry.dial_code}${clean}`; 
    this.onChange(this.innerValue);

    // Mostramos formato bonito
    const part1 = clean.substring(0, 3);
    const part2 = clean.substring(3, 6);
    const part3 = clean.substring(6, 10);
    this.displayValue = `+${this.selectedCountry.dial_code} ${part1} ${part2} ${part3}`;
  }
  
  validateZip() {
    if (!this.innerValue || this.innerValue.length !== 5) {
      this.isInvalid = true;
      this.errorMsg = 'Ingrese 5 dígitos primero';
      return;
    }
    /// api de CP
   this.ceveService.getListZipCode(this.innerValue, 1).toPromise()
      .then((resp: any) => {
         this.zipDataFound.emit(resp);
      }).catch((error: any) => {
        console.log(error)
      })
  }

  
  private handleTextFormat(val: string) {
    if (val.length < this.minLength || val.length > this.maxLength) {
      this.isInvalid = true;
      this.errorMsg = `Longitud debe ser entre ${this.minLength} y ${this.maxLength}`;
    }
    this.onChange(this.innerValue);
    this.displayValue = val;
  }
  
  private handleZipFormat(val: string) {
    if (val.length !== 5) {
      this.isInvalid = true;
      this.errorMsg = 'Código postal inválido (5 dígitos)';
    }
    else{
      this.validateZip(); // Validamos el CP al perder el foco
    }
    this.onChange(this.innerValue);
    this.displayValue = val;
  }
}
